import React, { useEffect, useState, useRef } from "react";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Button,
  Typography,
} from "@material-tailwind/react";
import moment from "moment";
import {
  Memory16Regular,
  ProductionRegular,
  Toolbox16Filled,
  Settings16Filled,
} from "@fluentui/react-icons";
import GlobalMaintenanceModal from "./MaintenanceModal";
import NextMaintenance from "./NextMaintenance";
import type { SystemType, Service } from "../../types/types";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../store";
import { fetchClusters } from "../../store/slices/clustersSlice";
import { fetchPodMetrics, PodMetric } from "../../store/slices/podMetricsSlice";
import { fetchMaintenanceStatus, deleteMaintenance } from "../../store/slices/maintenanceSlice";
import { fetchCriticalServices } from "../../store/slices/criticalServicesSlice";
import ServicesTab from "./ServicesTab";
import SettingsTab from "./SettingsTab";
import ScriptTab from "./ScriptTab";

const AdminPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [systemType, setSystemType] = useState<SystemType>("dev");
  const { clusters = [] } = useSelector((state: RootState) => state.clusters);
  const { metrics: podMetrics, loading: podMetricsLoading } = useSelector((state: RootState) => state.podMetrics);
  const { 
    activeSessions, 
    globalMaintenance
  } = useSelector((state: RootState) => state.maintenance);
  
  // Get critical services data from Redux store
  const { criticalServices } = useSelector((state: RootState) => state.criticalServices);

  // Track whether global maintenance is active
  const [isGlobalMaintenanceActive, setIsGlobalMaintenanceActive] = useState(false);

  // Maintenance info state for the header banner
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    status: 'none' as 'upcoming' | 'current' | 'none',
    date: "",
    time: "",
    remainingTime: "",
    isGlobal: false,
    serviceName: ""
  });

  // Fetch clusters on component mount
  useEffect(() => {
    dispatch(fetchClusters());
  }, [dispatch]);

  // Fetch pod metrics when system type changes
  useEffect(() => {
    if (systemType) {
      dispatch(fetchPodMetrics(systemType));
      dispatch(fetchMaintenanceStatus(systemType));
      dispatch(fetchCriticalServices(systemType));
    }
  }, [dispatch, systemType]);
  
  const [services, setServices] = useState<Service[]>([]);
  
  // Ref to track the last global maintenance state we processed
  const lastGlobalMaintenanceState = useRef<boolean | null>(null);
  
  // Update global maintenance status when globalMaintenance changes
  useEffect(() => {
    const isActive = globalMaintenance?.enabled || false;
    setIsGlobalMaintenanceActive(isActive);
    
    // Only update services if global maintenance state has changed
    // This prevents the infinite loop
    if (lastGlobalMaintenanceState.current !== isActive && services.length > 0) {
      // Update the ref to the current state
      lastGlobalMaintenanceState.current = isActive;
      
      if (isActive) {
        // When global maintenance becomes active, set all services to maintenance mode
        setServices(prevServices => 
          prevServices.map(service => ({
            ...service,
            maintenance: true
          }))
        );
      } else {
        // When global maintenance is turned off, ensure all services show as not in maintenance
        // This is a direct UI update to ensure visual consistency
        setServices(prevServices => 
          prevServices.map(service => ({
            ...service,
            maintenance: false
          }))
        );
      }
    }
    
    // Calculate and update maintenance info for header banner
    const calculateMaintenanceInfo = () => {
      // Helper function to safely get UTC timestamp from date string
      const getUTCTimestamp = (dateString: string | undefined | null): number | null => {
        if (!dateString) return null;
        
        try {
          // Ensure the date string has UTC indicator if missing
          if (!dateString.endsWith('Z') && !dateString.includes('+')) {
            dateString = dateString + 'Z';
          }
          
          // Parse using moment to ensure proper UTC handling
          const momentDate = moment.utc(dateString);
          const timestamp = momentDate.valueOf(); // Get milliseconds since epoch
          
          return timestamp;
        } catch (err) {
          console.error(`Error parsing date: ${dateString}`, err);
          return null;
        }
      };
      
      // Format remaining time as HH:MM:SS
      const formatRemainingTime = (milliseconds: number): string => {
        // Use direct division and modulo to avoid mutating the input
        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      };
      
      // Current time as UTC timestamp using moment for consistency
      const nowTimestamp = moment.utc().valueOf();
      
      // Process a maintenance session to get its status and details
      const processMaintenanceSession = (session: any) => {
        const startTimeTimestamp = getUTCTimestamp(session.start_time);
        const endTimeTimestamp = getUTCTimestamp(session.end_time);
        const isGlobal = session.type === 'global';
        
        // Get service name for display
        let serviceName = '';
        if (!isGlobal && session.services && session.services.length > 0) {
          // For service maintenance, use the first service name
          // First try to find a matching service in our services list for a nice display name
          const serviceContainer = session.services[0];
          const matchingService = services.find(s => 
            s.containerName?.toLowerCase() === serviceContainer.toLowerCase()
          );
          
          if (matchingService) {
            // Use the friendly display name from our services list
            serviceName = matchingService.name;
          } else {
            // Fallback to the container name with first letter capitalized
            serviceName = serviceContainer.charAt(0).toUpperCase() + serviceContainer.slice(1);
          }
          
          // If there are multiple services, indicate that
          if (session.services.length > 1) {
            serviceName += ` +${session.services.length - 1}`;
          }
        }
        
        // Determine if it's current or upcoming
        let status: 'current' | 'upcoming' | 'none' = 'none';
        let date = '';
        let time = '';
        let remainingTime = '';
        let priority = 0; // Higher number = higher priority
        
        // If session is enabled and end time is in the future, it's current
        if (session.enabled && endTimeTimestamp && endTimeTimestamp > nowTimestamp) {
          // If start time is in the future, it's upcoming despite being enabled
          if (startTimeTimestamp && startTimeTimestamp > nowTimestamp) {
            status = 'upcoming';
            const startTimeDate = moment(startTimeTimestamp).local();
            date = startTimeDate.format('DD.MM.YYYY');
            time = startTimeDate.format('HH:mm');
            // Priority: Global upcoming (3) > Service upcoming (1)
            priority = isGlobal ? 3 : 1;
          } else {
            // It's currently active
            status = 'current';
            const remainingMs = endTimeTimestamp - nowTimestamp;
            remainingTime = formatRemainingTime(remainingMs);
            // Priority: Global active (10) > Service active (5)
            priority = isGlobal ? 10 : 5;
          }
        }
        // If session is not enabled but has a future start time, it's upcoming
        else if (!session.enabled && startTimeTimestamp && startTimeTimestamp > nowTimestamp) {
          status = 'upcoming';
          const startTimeDate = moment(startTimeTimestamp).local();
          date = startTimeDate.format('DD.MM.YYYY');
          time = startTimeDate.format('HH:mm');
          // Priority: Global upcoming (3) > Service upcoming (1)
          priority = isGlobal ? 3 : 1;
        }
        
        return {
          session,
          status,
          date,
          time,
          remainingTime,
          isGlobal,
          serviceName,
          priority,
          startTimeTimestamp: startTimeTimestamp || Infinity,
          endTimeTimestamp: endTimeTimestamp || Infinity
        };
      };
      
      // Process all maintenance sessions
      const allSessions = [
        // Include global maintenance if it exists
        ...(globalMaintenance ? [globalMaintenance] : []),
        // Include all service maintenance sessions
        ...activeSessions
      ];
      
      // Process and filter valid sessions (those with status 'current' or 'upcoming')
      const processedSessions = allSessions
        .map(processMaintenanceSession)
        .filter(item => item.status !== 'none');
      
      
      if (processedSessions.length === 0) {
      setMaintenanceInfo({
        status: 'none',
        date: '',
        time: '',
        remainingTime: '',
        isGlobal: false,
        serviceName: ''
      });
        return;
      }
      
      // Sort by priority (highest first), then by:
      // - For current sessions: sort by closest end time (ascending)
      // - For upcoming sessions: sort by closest start time (ascending)
      processedSessions.sort((a, b) => {
        // First sort by priority (highest first)
        if (a.priority !== b.priority) {
          return b.priority - a.priority;
        }
        
        // Then sort by time
        if (a.status === 'current' && b.status === 'current') {
          // Sort current sessions by closest end time
          return a.endTimeTimestamp - b.endTimeTimestamp;
        } else if (a.status === 'upcoming' && b.status === 'upcoming') {
          // Sort upcoming sessions by closest start time
          return a.startTimeTimestamp - b.startTimeTimestamp;
        }
        
        // This should never happen as we already sorted by priority
        return 0;
      });
      
      // Get the highest priority session
      const topSession = processedSessions[0];
      
      // Set the maintenance info based on the highest priority session
      setMaintenanceInfo({
        status: topSession.status,
        date: topSession.date,
        time: topSession.time,
        remainingTime: topSession.remainingTime,
        isGlobal: topSession.isGlobal,
        serviceName: topSession.serviceName
      });
    };
    
    calculateMaintenanceInfo();
    
    // Update the remaining time every second for a more accurate countdown
    const timer = setInterval(calculateMaintenanceInfo, 1000);
    return () => clearInterval(timer);
    
  }, [globalMaintenance, activeSessions]); // Removed services from dependencies
  
    // Generate services from podMetrics and check maintenance status
  useEffect(() => {
    if (podMetrics && podMetrics.length > 0) {
      // Group pods by container name/service
      const serviceMap = new Map<string, PodMetric[]>();
      
      podMetrics.forEach(pod => {
        // Use display_name if available, otherwise container_name
        const serviceName = pod.display_name || pod.container_name;
        if (!serviceMap.has(serviceName)) {
          serviceMap.set(serviceName, []);
        }
        serviceMap.get(serviceName)?.push(pod);
      });
      
      // Check if a service is in maintenance
      const isServiceInMaintenance = (_serviceName: string, containerName: string) => {
        // If global maintenance is active, all services are in maintenance
        if (isGlobalMaintenanceActive || globalMaintenance?.enabled) {
          return true;
        }
        
        // Check if service is explicitly in any active session's services array
        // Use exact matching on container name only
        const result = activeSessions.some(session => {
          if (!session.type) return false;
          
          const serviceMatch = session.services.some(maintenanceName => {
            const maintenanceNameLower = maintenanceName.toLowerCase();
            const containerNameLower = containerName.toLowerCase();
            
            // Only match exactly with container_name - no partial matches
            const exactMatch = maintenanceNameLower === containerNameLower;
            
            if (exactMatch) {
            }
            
            return exactMatch;
          });
          
          return serviceMatch;
        });
        return result;
      };
      
      // Create a map of critical services for quick lookup
      const criticalMap: Record<string, boolean> = {};
      if (criticalServices && criticalServices.length > 0) {
        criticalServices.forEach(service => {
          if (service.enabled) {
            criticalMap[service.service_name] = true;
          }
        });
      }
      
      // Convert map to services array
      const newServices: Service[] = Array.from(serviceMap.entries()).map(([name, pods], index) => {
        // Determine status based on pods
        let status = "Online";
        if (pods.some(pod => pod.phase === 'Failed')) {
          status = "Failed";
        } else if (pods.some(pod => pod.phase === 'Pending')) {
          status = "Pending";
        } else if (pods.every(pod => pod.phase === 'Running')) {
          status = "Running";
        }
        
        // Get any existing service with the same name to preserve some states
        const existingService = services.find(s => s.name === name);
        
        // Use the container_name from the pod directly for maintenance matching
        const containerName = pods[0]?.container_name || "";
        
        // Check if this service is in maintenance list from API using container_name
        const inMaintenanceList = isServiceInMaintenance(name, containerName);
        
        // Check if this service is marked as critical
        const serviceName = containerName || name.toLowerCase();
        
        const isCritical = Boolean(
          criticalMap[serviceName] ||
          (existingService && existingService.isCritical)
        );
        
        return {
          id: index + 1,
          name,
          status,
          // Preserve maintenance state if service exists, otherwise use API state
          maintenance: existingService ? existingService.maintenance : inMaintenanceList,
          notes: pods[0]?.description || "No additional information",
          isCritical: isCritical,
          pods, // Keep pods reference for easy access
          isInMaintenanceList: inMaintenanceList,
          containerName // Store actual container name for debugging
        };
      });
      
      setServices(newServices);
    }
  }, [podMetrics, activeSessions, globalMaintenance, criticalServices]);

  // Component state variables
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("services");
  const [isGlobalMaintenanceModalOpen, setIsGlobalMaintenanceModalOpen] =
    useState(false);
  const [isScriptMode, setIsScriptMode] = useState(false);
  const [scriptCode, setScriptCode] = useState("");
  const [scriptTitle, setScriptTitle] = useState("");

  // Toggle maintenance for a specific service
  const toggleMaintenance = async (id: number) => {
    // Find the service
    const service = services.find((s) => s.id === id);
    if (!service) return;
    
    // Toggle the maintenance mode based on current state
    if (service.maintenance) {
      try {
        // Find the maintenance session ID for this service
        let sessionId: string | undefined;
        
        // Check active sessions to find the one for this service
        if (activeSessions && activeSessions.length > 0) {
          const containerName = service.containerName || '';
          const serviceSession = activeSessions.find(session => 
            session.type === 'service' && 
            // session.enabled && 
            session.services.some(s => s.toLowerCase() === containerName.toLowerCase())
          );
          
          if (serviceSession) {
            sessionId = serviceSession.id;
          }
        }
        
        if (sessionId) {
          // If found, delete the maintenance session
          await dispatch(deleteMaintenance({
            id: sessionId,
            cluster: systemType
          })).unwrap();
          
          // Force a refresh of maintenance status to ensure UI is in sync
          dispatch(fetchMaintenanceStatus(systemType));
          
          // Update UI optimistically
          setServices(prevServices =>
            prevServices.map((s) => (s.id === id ? { ...s, maintenance: false } : s))
          );
        } else {
          console.error(`Could not find maintenance session ID for service ${service.name}`);
        }
      } catch (error: any) {
        // Toast is now handled in the slice
        console.error(`Error toggling maintenance for service ${service.name}:`, error);
      }
      return;
    }

    // Otherwise, show the modal for activating maintenance
    setSelectedService(id);
    setIsGlobalMaintenanceModalOpen(true);
  };
  
  // Toggle global maintenance
  const toggleGlobalMaintenance = async () => {
    if (isGlobalMaintenanceActive) {
      try {
        // Immediately update UI state for better user feedback
        setIsGlobalMaintenanceActive(false);
        
        // Immediately update all service switches to off for visual consistency
        setServices(prevServices => 
          prevServices.map(service => ({
            ...service,
            maintenance: false
          }))
        );
        
        // If global maintenance is active, find and delete the global maintenance session
        if (globalMaintenance && globalMaintenance.id) {
          await dispatch(deleteMaintenance({
            id: globalMaintenance.id,
            cluster: systemType
          })).unwrap();
        } else {
          console.error("Could not find global maintenance session ID");
        }
        
        // Also delete all service-specific maintenance sessions
        // Check active sessions to find service maintenance sessions
        if (activeSessions && activeSessions.length > 0) {
          const serviceMaintenanceSessions = activeSessions.filter(session => 
            session.type === 'service' && session.enabled
          );
          
          if (serviceMaintenanceSessions.length > 0) {
            // Create a batch of promises for all service maintenance deletions
            const deletePromises = serviceMaintenanceSessions.map(session =>
              dispatch(deleteMaintenance({
                id: session.id,
                cluster: systemType
              }))
              .catch(err => {
                // Individual error handling - don't throw, just log
                console.error(`Error deleting maintenance session ${session.id}:`, err);
                // Don't reject the promise, let the other deletions continue
                return null;
              })
            );
            
            await Promise.allSettled(deletePromises);
          }
        }
        
        // Add a delay before refreshing data to ensure API has time to process all changes
        setTimeout(() => {
          // Force a refresh of pod metrics and maintenance status
          dispatch(fetchPodMetrics(systemType));
          dispatch(fetchMaintenanceStatus(systemType));
        }, 1000); // 1 second delay
        
      } catch (error: any) {
        console.error("Error toggling global maintenance:", error);
        setIsGlobalMaintenanceActive(true);
        dispatch(fetchMaintenanceStatus(systemType));
      }
    } else {
      // If global maintenance is not active, show the modal to enable it
      setSelectedService(null); // No specific service selected
      setIsGlobalMaintenanceModalOpen(true);
    }
  };

  const openScriptMode = (id: number) => {
    setSelectedService(id);

    // Get the service name
    const service = services.find((s) => s.id === id);
    const serviceName = service ? service.name : `Service ${id}`;

    // Set the title and initial code based on the action
    let title = `Configure ${serviceName} Script`;
    let code = `
    function restartService() {
      console.log("Restarting service");
    }
    `;

    setScriptTitle(title);
    setScriptCode(code);
    setIsScriptMode(true);
  };

  const handleSaveScript = (code: string) => {
    // Here you would typically send this data to your backend API
    console.log("Saving script", code);
    setIsScriptMode(false);
  };

  const handleCloseScript = () => {
    setIsScriptMode(false);
  };

  // Global maintenance is now handled by toggleGlobalMaintenance
  const handleGlobalMaintenanceSave = (data: {
    startTime: string;
    endTime: string;
    note: string;
    selectedServices: number[];
  }) => {
    // If this was triggered by toggling a single service
    if (
      selectedService !== null &&
      data.selectedServices.includes(selectedService)
    ) {
      // Always set maintenance to true when activating for a single service
      setServices(
        services.map((service) =>
          service.id === selectedService
            ? { ...service, maintenance: true }
            : service
        )
      );
    } else {
      // Apply maintenance mode to all selected services
      setServices(
        services.map((service) =>
          data.selectedServices.includes(service.id)
            ? { ...service, maintenance: true }
            : service
        )
      );
    }
  };

  const handleSystemTypeChange = (type: SystemType) => {
    setSystemType(type);
    // Clear current critical services first to avoid showing stale data
    setServices([]);
  };



  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="flex flex-col items-start pt-12 px-8 gap-2 bg-[#0C111D]">
        <div className="w-full flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <div className="flex items-center">
                          <div className="flex items-center gap-4">
              <div className="rounded-full flex items-center justify-center">
                {systemType === "dev" ? (
                  <svg
                    width="45"
                    height="32"
                    viewBox="0 0 61 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.40249 8.40847V33.6035H16.7925V25.2881H25.195V41.9935H0V0.00598145H25.195V8.40847H8.40249Z"
                      fill="#12B76A"
                    />
                    <path
                      d="M51.6473 25.2075V16.7925H43.2572V0H34.8547V42H60.0497V35.3651H60.0746V25.2199H51.6473V25.2075ZM51.6473 33.5975H43.2572V25.2075H51.6473V33.5975Z"
                      fill="#12B76A"
                    />
                  </svg>
                ) : (
                  <svg
                    width="45"
                    height="32"
                    viewBox="0 0 61 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.40249 8.4085V33.6035H16.7925V25.2882H25.195V41.9935H0V0.00601196H25.195V8.4085H8.40249Z"
                      fill="#DA1E48"
                    />
                    <path
                      d="M51.6473 25.2075V16.7925H43.2572V1.52588e-05H34.8547V42H60.0497V35.3651H60.0746V25.2199H51.6473V25.2075ZM51.6473 33.5975H43.2572V25.2075H51.6473V33.5975Z"
                      fill="#DA1E48"
                    />
                  </svg>
                )}
              </div>
              <Typography
                variant="h1"
                className="font-inter font-bold text-3xl text-[#FCFCFD]"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                Admin{" "}
                {systemType === "prod" ? "Production" : "Development"}{" "}
                System
              </Typography>
            </div>

              {/* System Type Selector */}
              <div>
                <Menu>
                  <MenuHandler>
                    <Button
                      variant="text"
                      className="flex items-center gap-2 bg-transparent text-white py-2 px-3 border-0 outline-none focus:outline-none active:outline-none hover:outline-none ring-0 focus:ring-0 active:ring-0 hover:ring-0 shadow-none focus:shadow-none active:shadow-none hover:shadow-none"
                      placeholder={undefined}
                      onResize={() => {}}
                      onResizeCapture={() => {}}
                      onPointerEnterCapture={() => {}}
                      onPointerLeaveCapture={() => {}}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className="h-6 w-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </Button>
                  </MenuHandler>
                  <MenuList
                    placeholder={undefined}
                    onResize={() => {}}
                    onResizeCapture={() => {}}
                    onPointerEnterCapture={() => {}}
                    onPointerLeaveCapture={() => {}}
                  >
                    {clusters.map((cluster) => (
                      <MenuItem
                        key={cluster}
                        onClick={() =>
                          handleSystemTypeChange(cluster as SystemType)
                        }
                        placeholder={undefined}
                        onResize={() => {}}
                        onResizeCapture={() => {}}
                        onPointerEnterCapture={() => {}}
                        onPointerLeaveCapture={() => {}}
                        className="hover:bg-[#E4E7EC] border-0 outline-none focus:outline-none active:outline-none hover:outline-none ring-0 focus:ring-0 active:ring-0 hover:ring-0 shadow-none focus:shadow-none active:shadow-none hover:shadow-none"
                      >
                        <div className="flex items-center gap-2 font-inter font-semibold text-base text-[#344054] hover:bg-[#E4E7EC]">
                          {cluster === "prod" ? (
                            <ProductionRegular className="h-4 w-4" />
                          ) : (
                            <Memory16Regular />
                          )}
                          {cluster === "prod"
                            ? "Production"
                            : "Development"}
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </div>
            <Typography
              className="font-inter font-normal text-sm text-[#FCFCFD]"
              placeholder={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              Service Checker -{" "}
              <span className="font-inter font-normal text-xs text-[#F2F4F7]">
                Provided by GridBit
              </span>
            </Typography>
          </div>

          {/* Next Maintenance Banner */}
          <div className="flex flex-col items-end gap-2">
            <NextMaintenance 
              status={maintenanceInfo.status}
              date={maintenanceInfo.date}
              time={maintenanceInfo.time}
              remainingTime={maintenanceInfo.remainingTime}
              isGlobal={maintenanceInfo.isGlobal}
              serviceName={maintenanceInfo.serviceName}
            />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-[#0C111D] text-white px-8">
        <div className="w-full mt-2">
          <div className="flex">
            <div
              onClick={() => {
                setActiveTab("services");
                // Don't hide script mode when clicking tabs
              }}
              className={`px-6 py-3 cursor-pointer transition-all duration-200 relative ${
                activeTab === "services" && !isScriptMode
                  ? "text-white"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              <Toolbox16Filled className="mr-1" />
              <span className="text-sm font-medium">Services</span>
              {activeTab === "services" && !isScriptMode && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white"></div>
              )}
            </div>
            <div
              onClick={() => {
                setActiveTab("settings");
                // Don't hide script mode when clicking tabs
              }}
              className={`px-6 py-3 cursor-pointer transition-all duration-200 relative ${
                activeTab === "settings" && !isScriptMode
                  ? "text-white"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              <Settings16Filled className="mr-1" />
              <span className="text-sm font-medium">Settings</span>
              {activeTab === "settings" && !isScriptMode && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white"></div>
              )}
            </div>
            {isScriptMode && (
              <div
                onClick={() => {
                  // Make the script tab clickable to show the script editor
                  setIsScriptMode(true);
                }}
                className="px-6 py-3 text-white relative cursor-pointer"
              >
                <span className="text-sm font-medium">{scriptTitle}</span>
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="p-8 flex-grow flex flex-col">
        {isScriptMode ? (
          <ScriptTab
            scriptCode={scriptCode}
            setScriptCode={setScriptCode}
            handleSaveScript={handleSaveScript}
            handleCloseScript={handleCloseScript}
            scriptTitle={scriptTitle}
          />
        ) : activeTab === "services" ? (
          <ServicesTab
            services={services}
            toggleMaintenance={toggleMaintenance}
            toggleGlobalMaintenance={toggleGlobalMaintenance}
            openScriptMode={openScriptMode}
            setServices={setServices}
            podMetricsLoading={podMetricsLoading}
            isGlobalMaintenanceActive={isGlobalMaintenanceActive}
            systemType={systemType}
          />
        ) : (
          <SettingsTab />
        )}
      </div>

      {/* Global Maintenance Modal */}
      <GlobalMaintenanceModal
        isOpen={isGlobalMaintenanceModalOpen}
        onClose={() => {
          setIsGlobalMaintenanceModalOpen(false);
          setSelectedService(null);
        }}
        onSave={handleGlobalMaintenanceSave}
        services={services.map(service => ({
          ...service,
          // Ensure containerName is passed to the modal
          containerName: service.containerName || service.name.toLowerCase()
        }))}
        selectedServiceId={selectedService}
      />
    </div>
  );
};

export default AdminPage;
